import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import type { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { isInsufficientBalanceError, openInsufficientBalanceModal } from '@/app/entities/insufficientBalance/model'
import type { ProductDetailResponseSchema } from '@/common/api/client/types.gen'
import { usePagination } from '@/common/hooks/usePagination'
import { useCurrentOrg } from '../../org/model'
import { useProduct } from './useProduct'

const DEFAULT_POLL_MS = 2000

const DEFAULT_TERMINAL_STATUSES = ['completed', 'failed', 'partially_completed'] as const

/** Префикс ключа списка; инвалидация по нему затрагивает все страницы и размеры. */
const listDataQueryKey = (flowKey: string, orgId: string | undefined, productId: string | undefined) =>
    ['product-flow', flowKey, 'list', orgId, productId] as const

const listQueryKey = (
    flowKey: string,
    orgId: string | undefined,
    productId: string | undefined,
    page: number,
    pageSize: number
) => [...listDataQueryKey(flowKey, orgId, productId), page, pageSize] as const

export { listQueryKey }

export const detailQueryKey = (flowKey: string, orgId: string | undefined, resourceId: string | null) =>
    ['product-flow', flowKey, 'detail', orgId, resourceId] as const

const isTerminalStatus = (status: string | undefined, terminal: readonly string[]): boolean => {
    if (!status) {
        return false
    }
    return terminal.includes(status)
}

type ProductFlowErrorResponse = {
    detail?: string
}

type UseProductFlowFormParams<TValues extends FieldValues, TMutationResult = unknown, TMutationVariables = unknown> = {
    defaultValues: DefaultValues<TValues>
    generationMutation: Pick<UseMutationResult<TMutationResult, Error, TMutationVariables>, 'isError' | 'error'>
}

export const getProductFlowErrorMessage = (error: unknown) => {
    if (!isAxiosError<ProductFlowErrorResponse>(error)) {
        return undefined
    }

    return error.response?.data?.detail
}

export const useProductFlowForm = <
    TValues extends FieldValues,
    TMutationResult = unknown,
    TMutationVariables = unknown,
>({
    defaultValues,
    generationMutation,
}: UseProductFlowFormParams<TValues, TMutationResult, TMutationVariables>): UseFormReturn<TValues> => {
    const form = useForm<TValues>({ defaultValues })

    useEffect(() => {
        if (!generationMutation.isError) {
            return
        }

        const errorMessage = getProductFlowErrorMessage(generationMutation.error)
        if (!errorMessage) {
            return
        }

        form.setError('root', { type: 'server', message: errorMessage })
    }, [form, generationMutation.error, generationMutation.isError])

    return form
}

export type ProductFlowConfig<TList, TDetail extends { status?: string }, TMutationVariables, TMutationResult> = {
    flowKey: string
    /** Загрузка списка (GET по товару; page/size — серверная пагинация) */
    fetchList: (ctx: { orgId: string; productId: string; page: number; pageSize: number }) => Promise<TList>
    /** ID ресурса для detail из данных списка (например items[0].id) */
    selectIdFromList: (list: TList | undefined) => string | null
    /** ID ресурса после успешной mutation (generation_id, group_id, …) */
    extractIdFromMutationResult: (result: TMutationResult) => string
    fetchDetail: (orgId: string, resourceId: string) => Promise<TDetail>
    runMutation: (params: {
        orgId: string
        productId: string
        product: ProductDetailResponseSchema | undefined
        variables: TMutationVariables
    }) => Promise<TMutationResult>
    terminalStatuses?: readonly string[]
    pollIntervalMs?: number
    enableLongPollingAfterSubmit?: boolean
}

export type ProductFlowState<TList, TDetail, TMutationVariables, TMutationResult> = {
    orgId: string | undefined
    productId: string | undefined
    product: ProductDetailResponseSchema | undefined
    isProductLoading: boolean
    /** Загрузка detail: первый fetch, лонг-полл до терминального status, запрос формы до завершения поллинга после mutation */
    detailQueryLoading: boolean
    /**
     * Отдельно от первичного fetch: detail с нетерминальным status, активная mutation,
     * или после submit ожидаем первый ответ detail по текущему generationId.
     */
    detailFormPipelineLoading: boolean
    listQuery: UseQueryResult<TList, Error>
    detailQuery: UseQueryResult<TDetail, Error>
    generationMutation: UseMutationResult<TMutationResult, Error, TMutationVariables>
    /** Выбранный id: из списка или из ответа mutation */
    generationId: string | null
    /** Запуск мутации без DOM-события (react-hook-form handleSubmit) */
    submitGeneration: (variables?: TMutationVariables) => void
    handleGenerationSubmit: (event: React.FormEvent<HTMLFormElement>, variables?: TMutationVariables) => void
    flowKey: string
    fetchDetail: (orgId: string, resourceId: string) => Promise<TDetail>
    pollIntervalMs: number
    terminalStatuses: readonly string[]
    /** Пагинация только для отображения списка (URL `listPage`) */
    listCurrentPage: number
    listPageSize: number
    handleListPageChange: (page: number) => void
    /** Первая страница списка (и при page≠1 — догрузка page 1) уже получена с сервера */
    generationListResolved: boolean
    /** Ответ списка page=1, по которому выбирается generationId */
    generationListData: TList | undefined
}

export const useProductFlow = <TList, TDetail extends { status?: string }, TMutationVariables, TMutationResult>(
    config: ProductFlowConfig<TList, TDetail, TMutationVariables, TMutationResult>
): ProductFlowState<TList, TDetail, TMutationVariables, TMutationResult> => {
    const {
        flowKey,
        fetchList,
        selectIdFromList,
        extractIdFromMutationResult,
        fetchDetail,
        runMutation,
        terminalStatuses = DEFAULT_TERMINAL_STATUSES,
        pollIntervalMs = DEFAULT_POLL_MS,
        enableLongPollingAfterSubmit = true,
    } = config

    const { data: currentOrg } = useCurrentOrg()
    const { productId } = useParams()
    const orgId = currentOrg?.id
    const { product, isLoading: isProductLoading } = useProduct()
    const queryClient = useQueryClient()

    const [submittedResourceId, setSubmittedResourceId] = useState<string | null>(null)

    const {
        currentPage: listCurrentPage,
        pageSize: listPageSize,
        handlePageChange: handleListPageChange,
    } = usePagination({
        paramName: 'listPage',
        pageSize: 10,
        resetDeps: [orgId, productId, flowKey],
    })

    useEffect(() => {
        setSubmittedResourceId(null)
    }, [productId, orgId, flowKey])

    const listQuery = useQuery({
        queryKey: listQueryKey(flowKey, orgId, productId, listCurrentPage, listPageSize),
        queryFn: () =>
            fetchList({
                orgId: orgId!,
                productId: productId!,
                page: listCurrentPage,
                pageSize: listPageSize,
            }),
        enabled: Boolean(orgId && productId),
    })

    const isListFirstPage = listCurrentPage === 1

    /** При просмотре не первой страницы дополнительно грузим страницу 1 — detail всегда по первому элементу списка. */
    const listFirstPageQuery = useQuery({
        queryKey: listQueryKey(flowKey, orgId, productId, 1, listPageSize),
        queryFn: () =>
            fetchList({
                orgId: orgId!,
                productId: productId!,
                page: 1,
                pageSize: listPageSize,
            }),
        enabled: Boolean(orgId && productId && !isListFirstPage),
    })

    const listForDetailId = isListFirstPage ? listQuery.data : listFirstPageQuery.data
    const listFirstId = selectIdFromList(listForDetailId)
    const generationId = submittedResourceId ?? listFirstId

    const detailQuery = useQuery({
        queryKey: detailQueryKey(flowKey, orgId, generationId),
        queryFn: async () => fetchDetail(orgId!, generationId!),
        enabled: Boolean(orgId && generationId),
        refetchInterval: query => {
            const status = query.state.data?.status
            if (isTerminalStatus(status, terminalStatuses)) {
                return false
            }
            if (!enableLongPollingAfterSubmit && submittedResourceId !== null) {
                return false
            }
            return pollIntervalMs
        },
    })

    const generationMutation = useMutation({
        mutationFn: async (variables: TMutationVariables) => {
            if (!orgId) {
                throw new Error('Организация не выбрана')
            }
            if (!productId) {
                throw new Error('Товар не найден')
            }
            if (!product) {
                throw new Error('Товар не загружен')
            }
            return runMutation({ orgId, productId, product, variables })
        },
        onSuccess: data => {
            const resourceId = extractIdFromMutationResult(data)
            setSubmittedResourceId(resourceId)
            handleListPageChange(1)
            void queryClient.invalidateQueries({ queryKey: listDataQueryKey(flowKey, orgId, productId) })
            void queryClient.invalidateQueries({
                queryKey: detailQueryKey(flowKey, orgId, resourceId),
            })
        },
        onError: error => {
            if (isInsufficientBalanceError(error)) {
                openInsufficientBalanceModal()
            }
        },
    })

    /**
     * Генерация «идёт», пока:
     * - ждём ответ мутации;
     * - после submit ждём первый detail по submittedResourceId;
     * - detail уже есть, но status ещё не терминальный (лонг-поллинг refetchInterval).
     * Не считаем загрузкой первичный fetch detail по списку без submit (submittedResourceId === null и data ещё нет).
     */
    const detailFormPipelineLoading = enableLongPollingAfterSubmit
        ? generationMutation.isPending ||
          (Boolean(orgId && generationId) &&
              !detailQuery.isError &&
              (detailQuery.data === undefined
                  ? submittedResourceId !== null
                  : !isTerminalStatus(detailQuery.data.status, terminalStatuses)))
        : generationMutation.isPending

    const detailQueryLoading = detailQuery.isLoading || detailFormPipelineLoading

    const generationListResolved =
        Boolean(orgId && productId) && listQuery.isFetched && (isListFirstPage ? true : listFirstPageQuery.isFetched)

    const submitGeneration = (variables?: TMutationVariables) => {
        generationMutation.mutate(variables as TMutationVariables)
    }

    const handleGenerationSubmit = (event: React.FormEvent<HTMLFormElement>, variables?: TMutationVariables) => {
        event.preventDefault()
        submitGeneration(variables)
    }

    return {
        orgId,
        productId,
        product,
        isProductLoading,
        detailQueryLoading,
        detailFormPipelineLoading,
        listQuery,
        detailQuery,
        generationMutation,
        generationId,
        submitGeneration,
        handleGenerationSubmit,
        flowKey,
        fetchDetail,
        pollIntervalMs,
        terminalStatuses,
        listCurrentPage,
        listPageSize,
        handleListPageChange,
        generationListResolved,
        generationListData: listForDetailId,
    }
}

type UseProductFlowListRowDetailQueryParams<TDetail extends { status?: string }> = {
    flowKey: string
    orgId: string | undefined
    resourceId: string
    fetchDetail: (orgId: string, resourceId: string) => Promise<TDetail>
    terminalStatuses: readonly string[]
    pollIntervalMs: number
    /** Например раскрыта ли строка списка */
    enabled: boolean
}

/** Детали по id из списка: тот же ключ и поллинг, что у `detailQuery` в `useProductFlow`. */
export const useProductFlowListRowDetailQuery = <TDetail extends { status?: string }>({
    flowKey,
    orgId,
    resourceId,
    fetchDetail,
    terminalStatuses,
    pollIntervalMs,
    enabled,
}: UseProductFlowListRowDetailQueryParams<TDetail>): UseQueryResult<TDetail, Error> => {
    return useQuery({
        queryKey: detailQueryKey(flowKey, orgId, resourceId),
        queryFn: () => fetchDetail(orgId!, resourceId),
        enabled: Boolean(orgId && resourceId && enabled),
        refetchInterval: query => {
            const status = query.state.data?.status
            if (isTerminalStatus(status, terminalStatuses)) {
                return false
            }
            return pollIntervalMs
        },
    })
}
